import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, useConfig } from '../../api/api';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import SendIcon from '@mui/icons-material/Send';
import Loader from '../special/Loader';
import MediaPreview from '../exo/explorer/MediaPreview';
import MarqueeText from '../special/MarqueeText';
import FormFields from '../form/FormFields';
import * as Yup from 'yup';
import { useUserPreferences } from '../../global/useUserPreferences';
import useFetch from '../../api/useFetch';
import { useEmailClient } from '../email-system/useEmailClient';
import ExoDraftManager from '../exo/ExoDraftManager';
import { RenderHtmlEmail } from '../email-system/ECEmailConversation';
import ExoAlert from '../exo/ExoAlert';

export const NewEmail = ({
  selectedFolder,
  onNewMail,
  oldEmail,
  type,
  blockRendering,
  startData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { emailCredentials } = useEmailClient();

  const { apiData } = useFetch(`text-blocks?type[eq]=signature`);

  const { t } = useTranslation();
  const config = useConfig();
  const { preferences, updatePreferences } = useUserPreferences();

  const [emailOptions, setEmailOptions] = useState(null);
  const [dataCollection, setDataCollection] = useState({});
  const [selectedCredentials, setSelectedCredentials] = useState(null);
  const [editMode, setEditMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCred, setSavedCred] = useState(null);

  useEffect(() => {
    if (!savedCred && emailCredentials) setSavedCred(emailCredentials);
  }, [emailCredentials]);

  useEffect(() => {
    if (savedCred) calculateOptions();
  }, [selectedFolder, savedCred]);

  function getLastCred() {
    var selectedCred = null;

    if (selectedFolder && !selectedFolder?.type) {
      savedCred?.data.forEach((cred) => {
        cred.inbox.folders.forEach((folder) => {
          if (folder.id === selectedFolder.id) {
            return (selectedCred = cred);
          }
        });
      });
    } else {
      selectedCred = savedCred?.data[0];
    }
    return selectedCred;
  }

  // actions
  function dataForBlank(email) {
    const selectedCred = getLastCred();
    setSelectedCredentials(selectedCred);

    return {
      credentialsId: selectedCred?.id,
      credentials: selectedCred,
      signature: findSignatureForCredentials(selectedCred),
      to: startData?.to,
    };
  }

  function dataForReply(email) {
    setEditMode(false);
    const cred = findCredentialsViaEmail(email) || getLastCred();
    setSelectedCredentials(cred);
    return {
      to: [email.from[0]],
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred?.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
    };
  }
  function dataForReplyAll(email) {
    setEditMode(false);

    const cred = findCredentialsViaEmail(email) || getLastCred();
    if (cred) setSelectedCredentials(cred);
    return {
      to: [email.from[0]],
      cc: email.cc,
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
    };
  }
  function dataForForwarding(email) {
    const cred = findCredentialsViaEmail(email) || getLastCred();
    if (cred) setSelectedCredentials(cred);
    return {
      subject: `${t('FWD')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      attachments: email.attachments,
      inlineAttachments: email.inlineAttachments,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
    };
  }

  function findCredentialsViaEmail(email) {
    const cred = savedCred?.data.find((obj) => {
      const matchesTo = email.to?.some((e) => e.email === obj.imapUsername);
      const matchesFrom = email.from?.some((e) => e.email === obj.imapUsername);
      return matchesTo || matchesFrom;
    });

    return cred || null;
  }

  function findSignatureForCredentials(cred) {
    var signature = null;
    const signatureMap = preferences?.emailSignatureMap || {};
    var signatureId = signatureMap[cred.imapUsername];

    if (!signatureId && apiData.data[0]) {
      signature = apiData.data[0];
      updatePreferences(
        '',
        {
          emailSignatureMap: {
            ...signatureMap,
            [cred.imapUsername]: apiData.data[0].id,
          },
        },
        true
      );
    }

    if (signatureId && !signature) {
      signature = apiData?.data.find((obj) => obj.id === signatureId);
    }

    return signature;
  }

  const typeMap = {
    blank: dataForBlank,
    reply: dataForReply,
    replyAll: dataForReplyAll,
    forwarding: dataForForwarding,
  };

  useEffect(() => {
    if (preferences && apiData && savedCred)
      setDataCollection(typeMap[type || 'blank'](oldEmail));
  }, [oldEmail, preferences, apiData, savedCred]);

  // form
  function calculateOptions() {
    var newEmailOptions = [];
    if (emailOptions) return;

    savedCred?.data.forEach((cred) => {
      const { color, id, imapUsername, label } = cred;
      newEmailOptions.push({
        label: (
          <Box className="flex items-center">
            <Box sx={{ bgcolor: color }} />
            <Typography fontWeight={600}>{label}</Typography>
            <span className="opacity-50 pl-3">{`<${imapUsername}>`}</span>
          </Box>
        ),
        value: id,
      });
    });
    setEmailOptions(newEmailOptions);
  }

  function sendEmail() {
    const fontUrl = `${config.apiUrl}/fonts/`;
    const defaultStyles = `
    <style>
        @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat.ttf') format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: normal;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat-Italic.ttf')
          format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: italic;
      }
      *{
        font-family: 'Montserrat';
        margin: 0;
      }
    </style>
    `;
    DBRequest({
      config,
      path: `imap-emails`,
      method: API_POST({
        ...dataCollection,
        htmlBody: `<body style="background-color:rgb(255,255,255); padding:10px;">${defaultStyles} ${dataCollection.htmlBody}</body>`,
      }),
      onLoading: setIsLoading,
      onResponse: onNewMail,
      onError: (error) =>
        setAlert({ open: true, type: 'error', message: error.message }),
    });
  }

  const fields = [
    {
      label: t('from'),
      key: 'credentialsId',
      options: emailOptions,
      type: 'customSelect',
    },
    {
      label: t('to'),
      key: 'to',
      type: 'participantsAutoComplete',
    },
    {
      label: t('cc'),
      key: 'cc',
      type: 'participantsAutoComplete',
    },
    {
      label: t('bcc'),
      key: 'bcc',
      type: 'participantsAutoComplete',
      allowSmartSelect: true,
    },
    {
      label: t('Attachments'),
      key: 'attachments',
      maxSizeKB: 25600,
      type: 'images',
      maxItems: 10,
    },
  ];

  // validation
  const emailSchema = Yup.object().shape({
    email: Yup.string().required('Email is required'),
  });

  const validationSchemaTop = Yup.object().shape({
    credentialsId: Yup.string().required(t('Is required')),

    to: Yup.array()
      .of(emailSchema)
      .when('bcc', (bcc, schema) => {
        const hasValidBcc =
          bcc &&
          bcc[0] &&
          bcc[0].some(
            (item) =>
              item?.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email)
          );
        return hasValidBcc ? schema : schema.min(1).required();
      }),
  });
  const validationSchemaBottom = Yup.object().shape({
    subject: Yup.string().required(t('Is required')),
    htmlBody: Yup.string().required(t('Is required')),
  });

  const [topIsValid, setTopIsValid] = useState(false);
  const [bottomIsValid, setBottomIsValid] = useState(false);

  const [defaultData, setDefaultData] = useState({});

  const [alert, setAlert] = useState({ type: 'error' });

  return (
    <Box
      className="flex flex-col lg:flex-row w-full gap-4 lg:h-full"
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ExoDraftManager
        dataCollection={dataCollection}
        draftType={'email'}
        startDataCollection={startData}
        setDataCollection={(e) => {
          setDataCollection(e);
          setDefaultData(e);
        }}
        titleFunction={(e) => e.subject}
        className=" z-10"
        onChange={(e) => setDataCollection({ ...dataCollection, ...e })}
      />
      <Box className="flex flex-col gap-4 h-full overflow-y-auto w-full max-w-[900px]">
        <Loader active={isLoading} />
        <Box className="grid grid-cols-2 w-full gap-4 items-start md:flex">
          <Button
            variant="contained"
            color="success"
            disableElevation
            onClick={sendEmail}
            sx={{ height: '46px' }}
            className=" col-span-2"
            disabled={!topIsValid || !bottomIsValid}
          >
            <SendIcon />
          </Button>
          <Box className="w-full col-span-2">
            <Collapse in={editMode}>
              <Box className="flex flex-col gap-4 pt-3 pb-2">
                <FormFields
                  dataCollection={dataCollection}
                  updateDataCollection={(data) => {
                    var cred = {};
                    if (data.credentialsId) {
                      cred = savedCred?.data.find(
                        (obj) => obj.id === data.credentialsId
                      );
                    }
                    setDataCollection({ ...dataCollection, ...data, ...cred });
                  }}
                  size="small"
                  fields={fields}
                  validationSchema={validationSchemaTop}
                  onValidationChange={setTopIsValid}
                />
                <Divider />
              </Box>
            </Collapse>

            <Collapse in={!editMode}>
              <Button
                onClick={() => setEditMode(true)}
                sx={{
                  bgcolor: colors.card,
                  display: 'block',
                  textAlign: 'left',
                  border: `2px solid ${
                    topIsValid ? 'transparent' : colors.warning
                  }`,
                  ':hover': {
                    bgcolor: colors.bgInput,
                  },
                }}
                className="w-full rounded-lg p-3"
              >
                <Typography fontSize={12}>
                  <span style={{ fontWeight: 500 }}>{t('from')}:</span>
                  <span className="opacity-50 pl-2">
                    {selectedCredentials?.imapUsername}
                  </span>
                </Typography>
                <Divider />
                <Typography fontSize={12} className="pt-2">
                  <span style={{ fontWeight: 500 }}>{t('to')}:</span>
                  {dataCollection?.to?.map((to, index) => (
                    <span className="opacity-50 pl-2">
                      {to.email}
                      {dataCollection?.to.length !== index + 1 ? ',' : ''}
                    </span>
                  ))}
                </Typography>
                {dataCollection?.cc?.length > 0 && (
                  <Typography fontSize={12}>
                    <span style={{ fontWeight: 500 }}>{t('cc')}:</span>
                    {dataCollection?.cc?.map((cc, index) => (
                      <span className="opacity-50 pl-2">
                        {cc.email}
                        {dataCollection?.cc.length !== index + 1 ? ',' : ''}
                      </span>
                    ))}
                  </Typography>
                )}
                {dataCollection?.attachments && (
                  <Box className="flex items-start w-full gap-2">
                    <Typography className="pr-2">
                      {t('Attachments')}:
                    </Typography>
                    <Box className="flex items-start w-full gap-2 flex-wrap">
                      {dataCollection?.attachments.map((att) => (
                        <Box className="flex gap-1 items-center">
                          <MediaPreview
                            src={att.thumbnailUrl || att.temporaryUrl}
                            size="30px"
                            mimeType={att.mimeType}
                            allowPreview
                          />
                          <MarqueeText text={att.title} width="200px" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Button>
            </Collapse>
          </Box>
        </Box>
        <Box
          className="flex flex-col gap-2 w-full h-full"
          onClick={() => setEditMode(false)}
        >
          <FormFields
            dataCollection={dataCollection}
            startDataCollection={defaultData}
            updateDataCollection={(data) =>
              setDataCollection({ ...dataCollection, ...data })
            }
            onPreventEnter={() => {}}
            fields={[
              {
                label: t('Subject'),
                key: 'subject',
                type: 'text',
                textBlockType: 'emailSubject',
              },
              {
                label: '',
                key: 'htmlBody',
                textBlockType: 'block',
                allowSignature: true,
                signatureKey: 'signature',
                type: 'richTextEditor',
                afterKey: 'additionalContent',
              },
            ]}
            validationSchema={validationSchemaBottom}
            onValidationChange={setBottomIsValid}
          />
        </Box>
      </Box>
      {oldEmail && (
        <Box
          className="h-full lg:w-[60vw] rounded-lg p-2 overflow-y-auto"
          sx={{
            bgcolor: 'rgba(235, 235, 235, 0.8)',
          }}
        >
          <Typography
            fontWeight={500}
            className="opacity-50"
            sx={{ color: 'rgb(30,30,30)' }}
          >
            {t('Previous messages')}
          </Typography>
          <RenderHtmlEmail
            htmlBody={
              oldEmail.htmlBody ||
              oldEmail.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>')
            }
            inlineAttachments={oldEmail?.inlineAttachments}
          />
        </Box>
      )}
      <ExoAlert
        alert={alert}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </Box>
  );
};
